import { Component, OnInit, OnDestroy, AfterViewInit, HostListener, inject } from "@angular/core";
import { Router } from "@angular/router";
import { TranslateService } from "@ngx-translate/core";
import { UntypedFormGroup, UntypedFormBuilder, Validators } from "@angular/forms";
import { Subject, Subscription } from "rxjs";
import { Store } from "@ngrx/store";
import { filter, take, tap } from "rxjs/operators";

import { PasswordRequiredError } from "@intocare-medapp/aws/http";
import { IcEnvironment } from "@intocare-medapp/environment";

import { SimpleToastService, SplashScreenService } from "../../shared/modules";
import { AuthModel, LanguageService } from "../../shared/index";
import { Credentials } from "../../shared/types";
import { PermissionDeniedError } from "../../shared/errors/permission-denied.error";
import { VersionError } from "app/shared/errors/version.error";
import { HomeModel } from "app/home/home.model";
import { MustEnableMfaError } from "app/shared/errors/must-enable-mfa.error";
import { MFARequiredError } from "@intocare-medapp/aws/http/entities";
import { EXPLICIT_SELECT_ORGANISATION } from "../constants";
import { selectIsAppVersionOutdated, selectLatestAppVersion } from "../../shared/state/selectors/app-details.selectors";
import { SignInWithUsernameActions } from "../state/actions/sign-in-with-username.actions";
import { UnknownProblemError } from "app/shared/errors/unknown-problem.error";
import { MustSetMainFunctionError } from "app/shared/errors/must-set-main-function.error";

@Component({
	selector: "ic-sign-in-with-username",
	templateUrl: "./sign-in-with-username.component.html",
	styleUrls: ["./sign-in-with-username.component.scss"],
	standalone: false,
})
export class SignInWithUsernameComponent implements OnInit, AfterViewInit, OnDestroy {
	private authModel = inject(AuthModel);
	private toastService = inject(SimpleToastService);
	private router = inject(Router);
	private splashscreenService = inject(SplashScreenService);
	private translateService = inject(TranslateService);
	private fb = inject(UntypedFormBuilder);
	private homeModel = inject(HomeModel);
	private languageService = inject(LanguageService);
	private icEnvironment = inject(IcEnvironment);
	private readonly store = inject(Store);

	private readonly destroy$ = new Subject<void>();
	private environment = this.icEnvironment.getEnvironment();
	appVersion = this.environment.APP_VERSION;
	form: UntypedFormGroup = this.fb.group({
		username: ["", Validators.required],
		password: ["", Validators.required],
	});
	// @ts-expect-error TS2564
	userActivity: ReturnType<typeof setTimeout>;
	userInactive = new Subject<boolean>();
	userInactiveSubscription: Subscription;
	protected isAppVersionOutdated$ = this.store.select(selectIsAppVersionOutdated(this.environment.APP_VERSION));

	constructor() {
		this.userInactiveSubscription = this.userInactive.subscribe((next: boolean) => {
			if (next) {
				console.log("user has been inactive for 2 minutes");
				this.form.reset();
			}
		});
	}

	clearInactivityTimeout() {
		if (this.userActivity) {
			// clear any existing timeout
			clearTimeout(this.userActivity);
		}
	}

	setInactivityTimeout() {
		this.clearInactivityTimeout();
		// start the timer over again 2 minutes
		this.userActivity = setTimeout(() => this.userInactive.next(true), 2 * 60 * 1000);
	}

	authenticate({ username, password }: Credentials) {
		if (this.form.invalid) {
			this.form.markAllAsTouched();
			return;
		}

		this.authModel.authenticate({ username: username.toLowerCase(), password }).subscribe({
			next: () => {
				this.router.navigate(["user-briefing"]);
			},
			error: (err) => {
				this.authModel.hideLoadingDialog();

				if (err instanceof PasswordRequiredError) {
					// We have to confirm our password first
					this.router.navigate(["auth/confirm-password"]);
					return;
				}

				if (err instanceof MustEnableMfaError) {
					this.router.navigate(["auth/mfa-activation"]);
					return;
				}

				if (err instanceof UnknownProblemError) {
					this.router.navigate(["auth/unknown-problem"]);
					return;
				}

				if (err instanceof MustSetMainFunctionError) {
					this.router.navigate(["auth/must-set-main-function"]);
					return;
				}

				if (err instanceof MFARequiredError) {
					this.router.navigate(["auth/mfa-entry"]);
					return;
				}

				let message = "auth.errors.unknown";
				let delay = 5000;

				if (err.code === "NetworkError") {
					message = "general.warnings.network_lost";
				}

				if (err instanceof PermissionDeniedError) {
					this.form.reset();

					message = "auth.errors.permission_denied";
					this.router.navigate(["select-organisation"]);
				}

				if (err instanceof VersionError) {
					this.form.reset();

					delay = 10000;
					message = "auth.errors.version_update_required";
					this.router.navigate(["select-organisation"]);
				}

				if (err.code === "NotAuthorizedException" || err.code === "UserNotFoundException" || err.status === 404) {
					message = "auth.errors.not_found";
				}

				this.toastService.error(this.translateService.instant(message), {
					ms: delay,
				});
			},
		});
	}

	get hasUsernameError() {
		const control = this.form.get("username");

		// @ts-expect-error TS18047
		return control.touched && control.invalid;
	}

	get hasPasswordError() {
		const control = this.form.get("password");

		// @ts-expect-error TS18047
		return control.touched && control.invalid;
	}

	ngAfterViewInit() {
		this.splashscreenService.hide();
	}

	ngOnInit() {
		// Make sure the routing history is cleared so the routing resets when logged back in.
		this.homeModel.unsetActiveRoute();
		this.store
			.select(selectLatestAppVersion)
			.pipe(
				take(1),
				filter((version) => !version),
				tap(() => this.store.dispatch(SignInWithUsernameActions.getLatestAppVersion())),
			)
			.subscribe();
	}

	ngOnDestroy(): void {
		this.clearInactivityTimeout();
		this.userInactiveSubscription.unsubscribe();
		this.destroy$.next();
		this.destroy$.complete();
	}

	@HostListener("window:mousemove") refreshUserState() {
		this.setInactivityTimeout();
	}

	@HostListener("window:keydown") refreshUserState2() {
		this.setInactivityTimeout();
	}

	@HostListener("window:scroll") refreshUserState3() {
		this.setInactivityTimeout();
	}

	@HostListener("window:click") refreshUserState4() {
		this.setInactivityTimeout();
	}

	@HostListener("touchstart") refreshUserState5() {
		this.setInactivityTimeout();
	}

	@HostListener("touchmove") refreshUserState6() {
		this.setInactivityTimeout();
	}

	selectOrganisation() {
		this.router.navigate(["select-organisation"], {
			state: {
				[EXPLICIT_SELECT_ORGANISATION]: true,
			},
		});
	}
}
